import { useEffect } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { BaseEmptyPage, Footer, colors } from 'design-system';
import {
  Button,
  ButtonBase,
  Card,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { logout, refresProfile } from 'src/store/profile/actions';
import { RootState } from 'src/store/rootReducer';
import Profile from 'src/models/Profile';
import LandingToolbar from './LandingToolbar/LandingToolbar';
import makeLandingStyles from './makeLandingStyles';

export const insuranceCards = [
  {
    name: 'life-insurance',
    title: 'Seguro de vida',
    description:
      'El seguro de vida de ASSA Term Plus que te permite cotizar hasta $300.000 sin exámenes médicos en cuestión de minutos.',
    img: 'life_insurance_img.svg',
    buttonLabel: 'Cotizar',
  },
  {
    name: 'car-insurance',
    title: 'Seguro de auto',
    description:
      'El seguro de auto de AUTO PX que te permite asegurar tu coche con las mejores coberturas en cuestión de minutos.',
    img: 'car_insurance_img.svg',
    buttonLabel: 'Cotizar',
  },
];

export const LandingScreen = ({
  logoutAction,
  profile,
  refreshProfileAction,
}: PropsFromRedux): JSX.Element => {
  const classes = makeLandingStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  useEffect(() => {
    refreshProfileAction();
  }, [refreshProfileAction]);

  const userName = profile ? `${profile.Name} ${profile.LastName}` : '';
  return (
    <BaseEmptyPage padding={0}>
      <div className={classes.mainContainer}>
        <LandingToolbar
          onLogoutClick={logoutAction}
          user={userName}
          data-testid="toolbar"
        />

        <div className={classes.landingContainer}>
          <Grid container justifyContent="center">
            <Grid item xs={11} md={8}>
              <Typography
                variant="h2"
                align="center"
                className={classes.fontTitle}
                data-testid="landing-title"
              >
                Cotizadores ASSAnet
              </Typography>

              <Typography
                variant="h2"
                align="center"
                className={classes.fontSubtitle}
                data-testid="landing-subtitle"
              >
                Selecciona una de nuestras opciones para realizar tu cotización
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            justifyContent="center"
            className={classes.insurancesContainer}
          >
            <Grid item xs={11} md={12}>
              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="flex-start"
                data-testid="landing-cards-container"
              >
                {insuranceCards.map(
                  ({ name, title, description, img, buttonLabel }) => (
                    <Card
                      className={classes.card}
                      key={name}
                      data-testid="landing-card"
                    >
                      <img
                        data-testid={`card-img-${name}`}
                        className={classes.insuranceImage}
                        alt={img}
                        src={`/svg/${img}`}
                      />

                      <Typography
                        variant="h2"
                        data-testid={`card-title-${name}`}
                        align="center"
                        className={classes.cardTitle}
                      >
                        {title}
                      </Typography>

                      <Typography
                        variant="h2"
                        align="center"
                        data-testid={`card-description-${name}`}
                        className={classes.cardSubtitle}
                      >
                        {description}
                      </Typography>

                      <Button
                        data-testid={`card-button-${name}`}
                        id="sales-web-button-life-insurance"
                        size="large"
                        className={classes.buttonFooter}
                      >
                        {buttonLabel}
                      </Button>
                    </Card>
                  ),
                )}
              </Grid>
            </Grid>
          </Grid>
        </div>
        <div className={classes.footerContainer}>
          <Grid container justifyContent="center">
            <Grid item xs={11} sm={8} md={6} lg={4}>
              <Typography
                variant="caption"
                align="center"
                className={classes.footerDescription}
                data-testid="landing-footer"
              >
                <div>
                  En caso de requerir asistencia puedes llamar a nuestra línea
                  de soporte o visitar nuestra página{' '}
                  <ButtonBase
                    onClick={(): Window | null =>
                      window.open(
                        'https://www.assanet.com',
                        'WindowName',
                        'noopener',
                      )
                    }
                    id="sales-web-website-button"
                    data-testid="website-button"
                    className={classes.website}
                    component="span"
                  >
                    <strong> www.assanet.com</strong>
                  </ButtonBase>
                </div>
              </Typography>
            </Grid>
          </Grid>
          <Footer
            data-testid="design-system-footer"
            rootStyles={{
              border: 'none',
              background: colors.madison,
              marginTop: 40,
            }}
            textColor={colors.white}
            IsMobile={isMobile}
            country="Costa Rica"
            linkText="Consulte nuestros términos y condiciones"
            linkHref="https://www.assanet.cr/condiciones-generales/#seguros-generales"
          />
        </div>
      </div>
    </BaseEmptyPage>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  profile?: Profile | null;
} => ({
  profile: state.profile.profile,
});

const reduxActions = {
  logoutAction: logout,
  refreshProfileAction: refresProfile,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(LandingScreen);
